import React, { useEffect, useMemo, useState } from "react";
import { useRecoilValue } from "recoil";
import { SummaryCard } from "./SummaryCard";
import { dashboardDataAtom } from "../state/dashboardData";
import { filtersAtom } from "../state/filter";
import { useAuth } from "./AuthContext";
import { API_BASE_URL } from "../config/api";

interface MemberDetails {
    securedstichRevenueShare: number;
    clientRevenueShare: number;
    companyName: string;
}

interface MemberSummary {
    memberUuid: string;
    securedstichRevenueShare: number;
    clientRevenueShare: number;
}

function normalizeDate(d: Date) {
    return new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
}

function getPurchaseDate(item: any): Date {
    const dt = item?.transactionDate || item?.createdAt;
    return new Date(dt);
}

/**
 * Filter out system negative product refund adjustments / hidden logs
 */
function isHiddenTransaction(item: any): boolean {
    const productPrice = parseFloat(item?.productPrice ?? "0");
    const carePrice = parseFloat(item?.carePrice ?? "0");
    const isProductNegative = productPrice < 0;
    const isCarePriceZero = carePrice === 0;
    const hasNoRoot = item?.rootUniqueId === null || item?.rootUniqueId === undefined || item?.rootUniqueId === "";
    const isUnsold = item?.sold === false;

    return isProductNegative && isCarePriceZero && hasNoRoot && isUnsold;
}

/**
 * Checks if an item is a replacement in an exchange chain (index > 0)
 */
function isReplacementItem(item: any, allItems: any[]): boolean {
    if (!item) return false;
    if (["ReturnedForExchange", "cancelled"].includes(item?.status)) return false;

    const itemRoot = item?.rootUniqueId || item?.uniqueId;
    const itemEventId = item?.eventId;

    if (!itemRoot && !itemEventId) return false;

    const chainItems = allItems.filter((other: any) => {
        if (["ReturnedForExchange", "cancelled"].includes(other?.status)) return false;

        const otherRoot = other?.rootUniqueId || other?.uniqueId;
        if (itemRoot && otherRoot && itemRoot === otherRoot) return true;
        if (itemEventId && other?.eventId && itemEventId === other.eventId) return true;
        if (item?.rootUniqueId && (other?.uniqueId === item.rootUniqueId || other?.rootUniqueId === item.rootUniqueId)) return true;
        if (other?.rootUniqueId && (item?.uniqueId === other.rootUniqueId || item?.rootUniqueId === other.rootUniqueId)) return true;

        return false;
    });

    if (chainItems.length <= 1) return false;

    chainItems.sort((a: any, b: any) => {
        const dateA = new Date(a.transactionDate || a.createdAt || 0).getTime();
        const dateB = new Date(b.transactionDate || b.createdAt || 0).getTime();
        if (dateA !== dateB) return dateA - dateB;
        return (a.id || 0) - (b.id || 0);
    });

    const itemIndex = chainItems.findIndex(
        (ci: any) => (ci.id && ci.id === item.id) || (ci.uniqueId && ci.uniqueId === item.uniqueId)
    );

    return itemIndex > 0;
}

/**
 * Replacement = created + sold + linked to exchange whose original exchanged row had sold === true
 */
function isCarePlusReplacement(item: any, allItems: any[]): boolean {
    if (item?.status !== "created" || item?.sold !== true) return false;

    const hasLink =
        (item?.eventId != null && item.eventId !== "") ||
        (item?.rootUniqueId != null && item.rootUniqueId !== "");
    if (!hasLink) return false;

    const originalExchanged =
        (item.eventId &&
            allItems.find(
                (i: any) => i?.eventId === item.eventId && i?.status === "exchanged",
            )) ||
        allItems.find(
            (i: any) =>
                i?.status === "exchanged" &&
                (i?.uniqueId === item.rootUniqueId || i?.rootUniqueId === item.rootUniqueId),
        );

    return Boolean(originalExchanged && originalExchanged.sold === true);
}

/**
 * ✅ OPT-IN (Based strictly on formula)
 * Total Opt-ins = (true + exchanged) + (true + created where rootuniqueId = null)
 */
function isOptIn(item: any): boolean {
    if (item?.sold === true && item?.status === "exchanged") {
        return true;
    }

    if (item?.sold === true && item?.status === "created") {
        const hasRoot =
            item?.rootUniqueId !== null &&
            item?.rootUniqueId !== undefined &&
            item?.rootUniqueId !== "";
        
        if (!hasRoot) {
            return true;
        }
    }

    return false;
}

// Cancellation reversal clone (negative carePrice, not part of an exchange event)
function isCancellationReversal(item: any): boolean {
    const carePrice = parseFloat(item?.carePrice ?? 0);

    if (!(carePrice < 0 && item?.sold === false)) return false;

    if (item?.status === "ReturnedForExchange") return false;

    return item?.status === "created" || item?.status === "cancelled";
}

/**
 * ✅ NOT OPT-IN 
 * Fresh original order where customer did NOT buy Care+ (sold === false).
 * Excludes:
 * - Returns & Refund logs (ReturnedForExchange)
 * - Cancellations & Reversals
 * - System negative rows (isHiddenTransaction)
 * - Exchange Replacements (index > 0 in exchange chain)
 */
function isNotOptIn(item: any, allItems: any[] = []): boolean {
    if (item?.sold !== false) return false;

    if (
        item?.status === "ReturnedForExchange" ||
        item?.status === "cancelled" ||
        isCancellationReversal(item) ||
        isHiddenTransaction(item)
    ) {
        return false;
    }

    // If it's a replacement item in an exchange chain, it's NOT a new original order
    if (isReplacementItem(item, allItems)) {
        return false;
    }

    return true;
}

/**
 * ✅ REVENUE ledger
 * (opt-ins + replacements + ReturnedForExchange + cancellation reversals + cancelled billed originals)
 */
function shouldCountRevenue(item: any, allItems: any[] = []): boolean {
    // 1. Standard / fresh opt-ins
    if (isOptIn(item)) return true;

    // 2. Exchange replacements (created + sold + rootUniqueId + original had Care+)
    if (isCarePlusReplacement(item, allItems)) return true;

    // 3. Exchange return reconciliation rows
    if (item?.status === "ReturnedForExchange") return true;

    // 4. Cancellation reversal clones
    if (isCancellationReversal(item)) return true;

    // 5. Original sale cancelled after being billed
    if (item?.sold === true && item?.status === "cancelled") return true;

    return false;
}

const GROUP_BY_UNIQUE_ID_MEMBERS = new Set([
    "656f9a98-a100-494d-ad90-ef4f6f0e1020",
    "2ef6bbd7-90f1-4889-aff5-d7a95347064c",
    "e86fcea5-b5bc-43e5-9286-9f05eceed7f2",
]);

export default function SummaryGrid() {
    const { user } = useAuth();
    const dashboardData = useRecoilValue(dashboardDataAtom);
    const filters = useRecoilValue(filtersAtom);

    const isAdmin = user?.userName === "omkark@securedstitch.com";
    const isAllMembers = isAdmin && !filters.member;

    const [memberDetails, setMemberDetails] = useState<MemberDetails | null>(null);
    const [loadingMember, setLoadingMember] = useState(true);
    const [allMembers, setAllMembers] = useState<MemberSummary[]>([]);

    useEffect(() => {
        if (isAllMembers) {
            setLoadingMember(false);
            return;
        }
        const memberUuid = (filters.member as string) || localStorage.getItem("memberUuid");
        if (!memberUuid) return;
        const fetchMemberDetails = async () => {
            try {
                setLoadingMember(true);
                const response = await fetch(`${API_BASE_URL}/api/crd/memberDetails`, {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({ memberUuid }),
                });
                setMemberDetails(await response.json());
            } catch (error) {
                setMemberDetails(null);
            } finally {
                setLoadingMember(false);
            }
        };
        fetchMemberDetails();
    }, [filters.member, isAllMembers]);

    useEffect(() => {
        if (!isAllMembers) return;
        fetch(`${API_BASE_URL}/api/member`)
            .then((r) => r.json())
            .then((data: MemberSummary[]) => setAllMembers(data))
            .catch(() => {});
    }, [isAllMembers]);

    const filteredData = useMemo(() => {
        let items = dashboardData || [];
        
        // Filter out hidden negative adjustments
        items = items.filter((item: any) => !isHiddenTransaction(item));

        if (!filters.fromDate || !filters.toDate) return items;
        const from = normalizeDate(filters.fromDate);
        const to = normalizeDate(filters.toDate);
        return items.filter((item: any) => {
            const d = normalizeDate(getPurchaseDate(item));
            return d >= from && d <= to;
        });
    }, [dashboardData, filters.fromDate, filters.toDate]);

    const allItems = dashboardData || [];

    const currentMemberUuid = useMemo(() => {
        if (isAdmin && filters.member) return filters.member as string;
        if (!isAdmin) return localStorage.getItem("memberUuid") || "";
        return "";
    }, [isAdmin, filters.member]);

    const shouldGroupByUniqueId = useMemo(() => {
        if (isAllMembers) return false;
        return GROUP_BY_UNIQUE_ID_MEMBERS.has(currentMemberUuid);
    }, [isAllMembers, currentMemberUuid]);

    const groupedByUniqueId = useMemo(() => {
        if (shouldGroupByUniqueId) {
            const map = new Map<string, any[]>();
            filteredData.forEach((item: any) => {
                // Isolate these so they don't incorrectly flag orders as True/False
                if (isCancellationReversal(item) || item?.status === "ReturnedForExchange") {
                    const key = `__REV_OR_RETURN__${item.id}`;
                    map.set(key, [item]);
                    return;
                }
                
                // Group strictly by exact uniqueId to separate 652010806 from 652010730
                const key = String(item.uniqueId ?? "").trim();
                if (!key) return;
                
                if (!map.has(key)) map.set(key, []);
                map.get(key)!.push(item);
            });
            return Array.from(map.values());
        } else {
            if (isAllMembers) {
                const map = new Map<string, any[]>();
                filteredData.forEach((item: any) => {
                    const memberUuid = item.memberUuid;
                    if (isCancellationReversal(item) || item?.status === "ReturnedForExchange") {
                        const key = `${memberUuid}__REV_OR_RETURN__${item.id}`;
                        map.set(key, [item]);
                        return;
                    }
                    const uniqueId = String(item.uniqueId ?? "").trim();
                    if (GROUP_BY_UNIQUE_ID_MEMBERS.has(memberUuid)) {
                        const key = `${memberUuid}__${uniqueId}`;
                        if (!map.has(key)) map.set(key, []);
                        map.get(key)!.push(item);
                    } else {
                        const key = `${memberUuid}__${item.id}`;
                        map.set(key, [item]);
                    }
                });
                return Array.from(map.values());
            }
            return filteredData.map((item: any) => [item]);
        }
    }, [filteredData, shouldGroupByUniqueId, isAllMembers]);

    // ✅ ORDER COUNTS
    const totalOptins = useMemo(() => {
        return groupedByUniqueId.filter((group) =>
            group.some((item: any) => isOptIn(item)),
        ).length;
    }, [groupedByUniqueId]);

    const totalNotOptins = useMemo(() => {
        return groupedByUniqueId.filter(
            (group) =>
                !group.some((item: any) => isOptIn(item)) &&
                group.some((item: any) => isNotOptIn(item, allItems)),
        ).length;
    }, [groupedByUniqueId, allItems]);

    const totalOrders = totalOptins + totalNotOptins;
    const optinRate =
        totalOrders > 0 ? ((totalOptins / totalOrders) * 100).toFixed(2) : "0.00";

    const totalRevenue = useMemo(() => {
        return groupedByUniqueId.reduce((sum, group) => {
            return (
                sum +
                group
                    .filter((item: any) => shouldCountRevenue(item, allItems))
                    .reduce((s: number, item: any) => s + (parseFloat(item.carePrice) || 0), 0)
            );
        }, 0);
    }, [groupedByUniqueId, allItems]);

    const ssSharePct = memberDetails?.securedstichRevenueShare ?? 0;
    const clientSharePct = memberDetails?.clientRevenueShare ?? 0;

    const { ssRevenue, partnerRevenue } = useMemo(() => {
        if (isAllMembers) {
            const memberMap = new Map(allMembers.map((m) => [m.memberUuid, m]));
            let ss = 0,
                partner = 0;
            groupedByUniqueId.forEach((group) => {
                const groupRevenue = group
                    .filter((item: any) => shouldCountRevenue(item, allItems))
                    .reduce((s: number, item: any) => s + (parseFloat(item.carePrice) || 0), 0);
                const memberUuid = group[0]?.memberUuid;
                const member = memberMap.get(memberUuid);
                if (member) {
                    ss += (groupRevenue * member.securedstichRevenueShare) / 100;
                    partner += (groupRevenue * member.clientRevenueShare) / 100;
                } else {
                    ss += groupRevenue;
                }
            });
            return { ssRevenue: ss, partnerRevenue: partner };
        }
        return {
            ssRevenue: (totalRevenue * ssSharePct) / 100,
            partnerRevenue: (totalRevenue * clientSharePct) / 100,
        };
    }, [
        isAllMembers,
        allMembers,
        groupedByUniqueId,
        totalRevenue,
        ssSharePct,
        clientSharePct,
        allItems,
    ]);

    const summaries = [
        { label: "Total Orders", value: totalOrders.toLocaleString() },
        { label: "Total Opt-ins", value: totalOptins.toLocaleString() },
        { label: "Opt-in Rate", value: `${optinRate}%` },
        { label: "Total Revenue", value: `₹${totalRevenue.toFixed(2)}` },
        {
            label: "Your Percentage Share",
            value: isAllMembers ? "Varies" : loadingMember ? "—" : `${clientSharePct}%`,
        },
        {
            label: "SS Percentage Share",
            value: isAllMembers ? "Varies" : loadingMember ? "—" : `${ssSharePct}%`,
        },
        {
            label: "Your Revenue Share",
            value: loadingMember ? "—" : `₹${partnerRevenue.toFixed(2)}`,
        },
        {
            label: "SS Revenue Share",
            value: loadingMember ? "—" : `₹${ssRevenue.toFixed(2)}`,
        },
    ];

    return (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 w-full">
            {summaries.map((s) => (
                <SummaryCard key={s.label} title={s.label} value={s.value} />
            ))}
        </div>
    );
}